import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import DocumentFirstStepHead from '../../BusinessV2/components/DocumentFirstStepHead';
import { createWrapperFieldRenderer } from '../../helpers/createWrapperFieldRenderer';
import { STEP_NUM } from '../../IPlushelper';
import { MIN_USERNAME_LENGTH } from '../../../components/userName/constants';
import {
    buildUserDetailsFieldOrder,
    handleValidation,
    hasValue,
    validatePhoneNumber,
    validateSSN,
    validateUserDetailsField,
} from './helper';
import { shouldShowField } from '../../../../../components/InputField/helper';
import { buildAutomationChildTestId } from '../../../../../components/InputField/testId';
import { VARIANT, variantClass } from '../../../../../util/variant';
import '../../BusinessV2/documentsVersion2.css';

export { handleValidation } from './helper';

const USER_DETAILS_TEST_ID = 'individual-plus-user-details-sectioned';

// Default section layout used when the served config does not carry
// wizardContent.UserDetails.uiSections (defensive: keeps the step usable even
// before the doc-first config is published to SysConfig).
const FALLBACK_SECTIONS = [
    { id: 'identity', title: 'Your identity', fields: ['firstName', 'middleName', 'lastName', 'gender', 'nationality', 'dob', 'taxId', 'ssn', 'Mother_Maiden_Name'] },
    { id: 'residence', title: 'Residential address', fields: ['address.street', 'address.buildingNumber', 'address.city', 'address.state', 'address.pinCode', 'address.country'] },
    { id: 'contact', title: 'Contact', fields: ['mobilePhone', 'email'] },
];

// Only the address street + building span the full row; everything else pairs up (see below).
const FULL_WIDTH_FIELDS = new Set(['address.street', 'address.buildingNumber']);

// col-6 on laptop/desktop, full-width on mobile — gender+nationality, dob+taxId(ssn),
// Mother's Maiden Name and mobilePhone+email each sit two-per-row on md+.
const HALF_WIDTH = 'col-12 col-md-6';

// Column widths that match the business document-first form (BusinessVersion2.json uiClassName) so
// the "About you" step lays out identically to Business Plus: the three name fields 3-across, the
// identity / contact fields 2-across, the address street + building full width, and city / state /
// zip / country as a 4-up row on desktop (2-up on mobile). A field's own uiClassName in the served
// config still wins — this is the default used when the config does not set one, so the layout is
// correct before IndividualVersion2.json is copied to SysConfig and stays config-driven (same
// approach as business) once it is.
const FIELD_COLUMN_CLASS = {
    firstName: 'col-12 col-md-4',
    middleName: 'col-12 col-md-4',
    lastName: 'col-12 col-md-4',
    gender: HALF_WIDTH,
    nationality: HALF_WIDTH,
    dob: HALF_WIDTH,
    taxId: HALF_WIDTH,
    ssn: HALF_WIDTH,
    Mother_Maiden_Name: HALF_WIDTH,
    mobilePhone: HALF_WIDTH,
    email: HALF_WIDTH,
    'address.street': 'col-12',
    'address.buildingNumber': 'col-12',
    'address.city': 'col-6 col-md-3',
    'address.state': 'col-6 col-md-3',
    'address.pinCode': 'col-6 col-md-3',
    'address.country': 'col-6 col-md-3',
};

const UserDetailsSectionedV2 = ({
    asLabel,
    hideButtons,
    data,
    handleChangeViaObject,
    handleUpdate,
    hasError,
    setHasError,
    setErrorMessage = () => { },
    loading,
    setLoading,
    cyclosFields,
    jsonConfig,
    onOtpVerify,
    handlePrev,
    pageNum,
    stepCount,
    stepLabel = 'About you',
    variant = VARIANT.V2,
    // Doc-first is broker-only; default true so username is skipped and OTP is not forced.
    isBrokerMode = true,
}) => {
    const { showError } = useContext(DialogContext);
    const { t } = useTranslation();
    const [isValidSSN, setIsValidSSN] = useState(true);
    const [centralErrors, setCentralErrors] = useState({});
    const [shouldPerformOtpVerification, setShouldPerformOtpVerification] = useState(false);

    const wizardContent = useMemo(
        () => jsonConfig?.wizardContent?.UserDetails || {},
        [jsonConfig],
    );
    const sections = useMemo(() => {
        const configured = Array.isArray(wizardContent?.uiSections) ? wizardContent.uiSections : null;
        return (configured && configured.length ? configured : FALLBACK_SECTIONS);
    }, [wizardContent]);

    const getWrapperValidationError = useCallback(
        ({ jsonField, value, dataObj: currentData }) => validateUserDetailsField(jsonField, value, currentData),
        [],
    );

    const renderWrapperField = createWrapperFieldRenderer({
        fieldMap: cyclosFields,
        dataObj: data,
        disabled: asLabel,
        showError: hasError,
        defaultOnChange: handleChangeViaObject,
        defaultCustomValidation: getWrapperValidationError,
        variant,
    });

    const resolveFieldColumnClass = (field) => {
        const configured = cyclosFields?.[field]?.uiClassName || FIELD_COLUMN_CLASS[field];
        if (configured) return configured;
        return FULL_WIDTH_FIELDS.has(field) ? 'col-12' : 'col-12 col-sm-6';
    };

    const perFieldOptions = (field) => {
        // mt-3 gives every cell the same top margin (uniform vertical rhythm + a shared baseline for
        // inputs / selects / the phone input-group); the row's gx-3 matches it horizontally so the
        // gaps between fields are even on both axes.
        const className = `${resolveFieldColumnClass(field)} mt-3`;
        if (field === 'mobilePhone') {
            return {
                className,
                customOperator: setShouldPerformOtpVerification,
                customProp: onOtpVerify,
                onError: (v) => setCentralErrors((prev) => ({ ...prev, mobilePhone: v })),
            };
        }
        if (field === 'email') {
            return { className, disabled: true };
        }
        if (field === 'ssn') {
            return {
                className,
                customOperator: setIsValidSSN,
                customProp: { notifyParent: setIsValidSSN },
            };
        }
        return { className };
    };

    const renderSectionField = (field) => {
        const showIf = cyclosFields?.[field]?.showIf;
        if (showIf && !shouldShowField(data, showIf)) return null;
        return renderWrapperField(field, perFieldOptions(field));
    };

    const handleSubmit = () => {
        const shouldRequirePinCode = shouldShowField(data, cyclosFields?.['address.pinCode']?.showIf);
        const shouldRequireSSN = shouldShowField(data, cyclosFields?.ssn?.showIf);
        const shouldRequireTaxId = shouldShowField(data, cyclosFields?.taxId?.showIf);
        const orderedFields = buildUserDetailsFieldOrder({
            includePinCode: shouldRequirePinCode,
            includeUsername: !isBrokerMode,
            includeTaxId: shouldRequireTaxId,
            includeSSN: shouldRequireSSN,
            streetBeforeBuilding: true,
        });

        // Validate/toast in the on-screen (uiSections) order, not buildUserDetailsFieldOrder order,
        // so the first error shown is the first field the user actually sees as invalid. The identity
        // fields (gender/nationality/dob/taxId/ssn) render ABOVE the address block, but
        // buildUserDetailsFieldOrder lists the address first. Only the ORDER changes — the field set
        // still comes from buildUserDetailsFieldOrder (its include flags). Fields not placed by any
        // section (e.g. cyclosUserName) keep their original relative order at the end.
        const renderOrder = sections.flatMap((section) => (Array.isArray(section.fields) ? section.fields : []));
        const validationOrder = [
            ...renderOrder.filter((field) => orderedFields.includes(field)),
            ...orderedFields.filter((field) => !renderOrder.includes(field)),
        ];
        const errors = [];

        validationOrder.forEach((field) => {
            const fieldValue = _.get(data, field);
            if (field === 'mobilePhone') {
                // The phone field defers its own validation while the input is focused, so data and
                // centralErrors can lag when the user types then clicks Next without blurring. Read the
                // live input value so a valid number is not rejected on the first click.
                const liveInput = typeof document !== 'undefined'
                    ? document.querySelector('.fv_phone-validation-container input[type="number"]')
                    : null;
                const effectivePhone = (liveInput && liveInput.value !== '') ? liveInput.value : fieldValue;
                const phoneReport = centralErrors?.mobilePhone;
                const reportedMsg = typeof phoneReport === 'string' ? phoneReport : phoneReport?.errorMsg;
                if (!validatePhoneNumber(effectivePhone)) {
                    errors.push('Please enter a valid Mobile Phone');
                } else if (reportedMsg && reportedMsg.trim() && !/please enter valid/i.test(reportedMsg)) {
                    // Locally valid but the field reported a specific (backend) error, e.g. already in use.
                    errors.push(reportedMsg);
                } else if (!isBrokerMode && (shouldPerformOtpVerification ? !data?.phoneVerifiedViaOTP : false)) {
                    errors.push(t('Please verify mobile phone with OTP'));
                }
            } else if (field === 'cyclosUserName' && hasValue(fieldValue) && String(fieldValue).trim().length < MIN_USERNAME_LENGTH) {
                errors.push(`Username must be at least ${MIN_USERNAME_LENGTH} characters long.`);
            } else if (field === 'ssn' && hasValue(fieldValue) && !validateSSN(fieldValue)) {
                errors.push('Please enter a valid SSN number.');
            } else {
                const fieldError = validateUserDetailsField(cyclosFields?.[field], fieldValue, data)
                    || handleValidation(field, fieldValue, cyclosFields?.[field]);
                if (fieldError) errors.push(fieldError);
            }
        });

        if (shouldRequireSSN && hasValue(data?.ssn) && !isValidSSN) {
            errors.push(t(handleValidation('ssn', data?.ssn, cyclosFields?.ssn)));
        }

        if (errors.length > 0) {
            setHasError(true);
            setErrorMessage(errors.join(' '));
            showError(errors[0]);
            return undefined;
        }

        setHasError(false);
        return handleUpdate({ nextStep: STEP_NUM.DECLARATION_FORM });
    };

    return (
        <div
            className={`docfirst ${variantClass(variant)}`.trim()}
            data-testid={USER_DETAILS_TEST_ID}
        >
            <DocumentFirstStepHead
                stepIndex={pageNum}
                stepCount={stepCount}
                stepLabel={stepLabel}
                title={wizardContent?.hero?.title || 'About you'}
                intro={wizardContent?.hero?.description
                    || 'Values read from your documents are pre-filled below. Please confirm or correct each one.'}
                dataTestId={buildAutomationChildTestId(USER_DETAILS_TEST_ID, 'head')}
            />

            <div className="docfirst__card">
                {sections.map((section) => (
                    <div
                        className="docfirst__group"
                        key={section.id}
                        data-testid={buildAutomationChildTestId(USER_DETAILS_TEST_ID, `section-${section.id}`)}
                    >
                        {section.title && (
                            <h3 className="docfirst__panel-title mb-1">{t(section.title)}</h3>
                        )}
                        {section.description && (
                            <p className="docfirst__panel-body mb-2">{t(section.description)}</p>
                        )}
                        <div className="row gx-3">
                            {(Array.isArray(section.fields) ? section.fields : []).map((field) => (
                                <React.Fragment key={field}>{renderSectionField(field)}</React.Fragment>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {!hideButtons && (
                <div className="mt-4" data-testid={buildAutomationChildTestId(USER_DETAILS_TEST_ID, 'footer')}>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={handlePrev}
                        step={1}
                        // About you is step 2 of the doc-first flow (Documents precedes it), but this
                        // ButtonGroupNew is a single-step group with step=1. Without this flag the Back
                        // button is suppressed on step 1, stranding the user with no way back to Documents.
                        showBackOnFirstStep={Boolean(handlePrev)}
                        showPageNum={false}
                        pageNum={1}
                        hideBackButton={!handlePrev}
                        isDisabled={asLabel}
                        asLabel={false}
                        loading={loading}
                        setLoading={setLoading}
                        testId={buildAutomationChildTestId(USER_DETAILS_TEST_ID, 'navigation')}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(UserDetailsSectionedV2);
